import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Dialog, Input } from '@artosera/ui';
import { schemaEcritureFicheFacturation } from '@artosera/shared';
import { marketplaceConsoleApi, type FicheFacturationVue } from '../../lib/addons.js';
import { usePeut } from '../../auth/usePeut.js';
import { modeDemo } from '../../lib/api.js';
import styles from '../../styles/form.module.css';
import page from '../../styles/page.module.css';

export function FicheFacturation({ idGalerie, fermee }: { idGalerie: string; fermee: boolean }) {
  const { t } = useTranslation('console');
  const peut = usePeut();
  const requete = useQuery({
    queryKey: ['console', 'galerie', idGalerie, 'fiche-facturation'],
    queryFn: () => marketplaceConsoleApi.fiche(idGalerie),
    enabled: peut('console:read-addon-sales'),
  });
  if (!peut('console:read-addon-sales')) return null;
  return (
    <section className={page.panneau}>
      <h2>{t('addons.facturation.titre')}</h2>
      {requete.isPending ? <p>{t('addons.charger')}</p> : null}
      {requete.isError ? <p role="alert">{t('addons.facturation.erreur')}</p> : null}
      {requete.data ? (
        <Formulaire
          key={`${idGalerie}:${requete.data.revisionFicheFacturation}`}
          idGalerie={idGalerie}
          donnees={requete.data}
          modifiable={peut('console:manage-addon-sales') && !fermee && !modeDemo()}
        />
      ) : null}
    </section>
  );
}

function Formulaire({
  idGalerie,
  donnees,
  modifiable,
}: {
  idGalerie: string;
  donnees: FicheFacturationVue;
  modifiable: boolean;
}) {
  const { t, i18n } = useTranslation('console');
  const client = useQueryClient();
  const [verification, setVerification] = useState(false);
  const [motif, setMotif] = useState('');
  const [invalide, setInvalide] = useState(false);
  const fiche = donnees.ficheFacturation;
  const mutation = useMutation({
    mutationFn: (
      geste: { type: 'fiche'; corps: unknown } | { type: 'verification'; motif: string },
    ) =>
      geste.type === 'verification'
        ? marketplaceConsoleApi.verifierTva(idGalerie, {
            motif: geste.motif,
            revisionFicheFacturation: donnees.revisionFicheFacturation,
          })
        : marketplaceConsoleApi.ecrireFiche(idGalerie, geste.corps),
    onSuccess: (resultat) => {
      client.setQueryData(['console', 'galerie', idGalerie, 'fiche-facturation'], resultat);
    },
    onError: () => {
      void client.invalidateQueries({
        queryKey: ['console', 'galerie', idGalerie, 'fiche-facturation'],
      });
    },
  });
  const champs = [
    'raisonSociale',
    'ligne1',
    'ligne2',
    'codePostal',
    'ville',
    'pays',
    'siren',
    'tvaIntracom',
    'emailFacturation',
  ] as const;
  function valeur(champ: (typeof champs)[number]): string {
    if (
      champ === 'ligne1' ||
      champ === 'ligne2' ||
      champ === 'codePostal' ||
      champ === 'ville' ||
      champ === 'pays'
    )
      return fiche?.adresse[champ] ?? '';
    return fiche?.[champ] ?? '';
  }
  return (
    <>
      <p>
        {t(fiche?.tvaVerifieeLe ? 'addons.facturation.verifiee' : 'addons.facturation.nonVerifiee')}
        {fiche?.tvaVerifieeLe
          ? ` · ${new Date(fiche.tvaVerifieeLe).toLocaleString(i18n.language)} · ${t(`addons.facturation.sources.${fiche.tvaVerifieePar}`)}`
          : ''}
      </p>
      <form
        className={styles.grille}
        onSubmit={(event) => {
          event.preventDefault();
          if (!modifiable || mutation.isPending) return;
          const valeurs = new FormData(event.currentTarget);
          const texte = (champ: string) => String(valeurs.get(champ) ?? '').trim();
          const resultat = schemaEcritureFicheFacturation.safeParse({
            raisonSociale: texte('raisonSociale'),
            adresse: {
              ligne1: texte('ligne1'),
              ...(texte('ligne2') ? { ligne2: texte('ligne2') } : {}),
              codePostal: texte('codePostal'),
              ville: texte('ville'),
              pays: texte('pays'),
            },
            ...(texte('siren') ? { siren: texte('siren') } : {}),
            ...(texte('tvaIntracom') ? { tvaIntracom: texte('tvaIntracom') } : {}),
            emailFacturation: texte('emailFacturation'),
            revisionFicheFacturation: donnees.revisionFicheFacturation,
          });
          setInvalide(!resultat.success);
          if (resultat.success) mutation.mutate({ type: 'fiche', corps: resultat.data });
        }}
      >
        {champs.map((champ) => (
          <label className={styles.champ} key={champ}>
            {t(`addons.facturation.${champ}`)}
            <Input
              name={champ}
              defaultValue={valeur(champ)}
              type={champ === 'emailFacturation' ? 'email' : 'text'}
              maxLength={champ === 'pays' ? 2 : 254}
              required={!['ligne2', 'siren', 'tvaIntracom'].includes(champ)}
              readOnly={!modifiable}
            />
          </label>
        ))}
        {invalide ? <p role="alert">{t('addons.facturation.invalide')}</p> : null}
        {mutation.isError && !verification ? <p role="alert">{t('addons.echec')}</p> : null}
        {modifiable ? (
          <Button type="submit" disabled={mutation.isPending}>
            {t('addons.enregistrer')}
          </Button>
        ) : null}
      </form>
      {modifiable && fiche?.tvaIntracom ? (
        <Button
          disabled={mutation.isPending}
          onClick={() => {
            mutation.reset();
            setMotif('');
            setVerification(true);
          }}
        >
          {t('addons.facturation.verifier')}
        </Button>
      ) : null}
      <Dialog
        open={verification}
        onOpenChange={(ouvert) => {
          if (!mutation.isPending) setVerification(ouvert);
        }}
        title={t('addons.facturation.verifier')}
        closeLabel={t('addons.annuler')}
      >
        <form
          className={styles.champ}
          onSubmit={(event) => {
            event.preventDefault();
            if (!mutation.isPending && motif.trim())
              mutation.mutate({ type: 'verification', motif });
          }}
        >
          <p>{fiche?.tvaIntracom}</p>
          <label>
            {t('addons.motif')}
            <Input
              required
              maxLength={500}
              value={motif}
              onChange={(e) => setMotif(e.target.value)}
            />
          </label>
          {mutation.isError ? <p role="alert">{t('addons.echec')}</p> : null}
          <Button type="submit" disabled={mutation.isPending || !motif.trim()}>
            {t('addons.facturation.verifier')}
          </Button>
        </form>
      </Dialog>
    </>
  );
}
